import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Eye, EyeOff, Loader2, Lock, Mail } from "lucide-react";
import { toast } from "sonner";

import { useAuth } from "../../context/AuthContext";
import useSEO from "../../lib/useSEO";
import { formatApiError } from "../../lib/api";

export default function AdminLogin() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useSEO({ title: "Admin Login — Ace Sublimasi Nusantara" });

  useEffect(() => {
    if (user) {
      const to = location.state?.from || "/admin";
      navigate(to, { replace: true });
    }
  }, [user, location.state, navigate]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      await login(email, password);
      toast.success("Login berhasil");
      navigate(location.state?.from || "/admin", { replace: true });
    } catch (err) {
      toast.error(
        formatApiError(err?.response?.data?.detail) ||
          "Gagal login — periksa koneksi."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      data-testid="admin-login-page"
      className="min-h-screen bg-[#050505] flex flex-col items-center justify-center px-4"
    >
      <Link
        to="/"
        className="mb-8 font-display text-3xl uppercase text-white"
        data-testid="admin-login-home-link"
      >
        ACE<span className="text-[#CCFF00]">.</span>
        <span className="text-[10px] uppercase tracking-[0.25em] text-[#A3A3A3] ml-2">
          Sublimasi Nusantara
        </span>
      </Link>

      <form
        onSubmit={onSubmit}
        data-testid="admin-login-form"
        className="w-full max-w-md border border-[#262626] p-8 bg-[#0a0a0a]"
      >
        <p className="text-xs font-bold tracking-[0.3em] uppercase text-[#CCFF00] mb-3">
          / Admin Access
        </p>
        <h1 className="font-display text-3xl uppercase text-white mb-6">
          Masuk ke Dashboard
        </h1>

        <label className="text-xs font-bold uppercase tracking-widest text-[#CCFF00]">
          Email
        </label>
        <div className="mt-2 flex border border-[#262626] focus-within:border-[#CCFF00] transition-colors">
          <span className="px-3 flex items-center text-[#A3A3A3]">
            <Mail className="w-4 h-4" />
          </span>
          <input
            type="email"
            required
            autoComplete="email"
            data-testid="admin-email-input"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="admin@..."
            className="flex-1 bg-transparent text-sm text-white py-3 outline-none pr-3"
          />
        </div>

        <label className="mt-5 text-xs font-bold uppercase tracking-widest text-[#CCFF00] block">
          Password
        </label>
        <div className="mt-2 flex border border-[#262626] focus-within:border-[#CCFF00] transition-colors">
          <span className="px-3 flex items-center text-[#A3A3A3]">
            <Lock className="w-4 h-4" />
          </span>
          <input
            type={showPwd ? "text" : "password"}
            required
            autoComplete="current-password"
            data-testid="admin-password-input"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="••••••••"
            className="flex-1 bg-transparent text-sm text-white py-3 outline-none"
          />
          <button
            type="button"
            onClick={() => setShowPwd((s) => !s)}
            data-testid="admin-toggle-password"
            className="px-3 text-[#A3A3A3] hover:text-white"
            aria-label="toggle password"
          >
            {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </button>
        </div>

        <button
          type="submit"
          disabled={submitting}
          data-testid="admin-login-submit"
          className="btn-primary w-full mt-8 disabled:opacity-60 disabled:cursor-wait"
        >
          {submitting ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" /> Memproses...
            </>
          ) : (
            "Login"
          )}
        </button>

        <p className="mt-6 text-xs text-[#A3A3A3] text-center">
          Khusus tim internal Ace Sublimasi Nusantara.
        </p>
      </form>
    </div>
  );
}
